import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Configuration sections shown by the Servio modal tabs. Kept as
// `Record<string, unknown>` rather than `any` so reducer payloads stay typed.
export type ServioSection = Record<string, unknown>;

// ─── Full State ─────────────────────────────────────────
// Only the sections rendered by the modal tabs are kept on the slice. The
// other Servio domains (restaurant, menu, payment, users, template) live
// outside this slice and can be added back if and when their tabs ship.
export type ServioState = {
    langues: ServioSection;
    home: ServioSection;
    commandes: ServioSection;
    notifications: ServioSection;
};

// ─── Initial State ──────────────────────────────────────
// DEFAULT_CONFIG used when the API answers
//   { error: true, message: "Aucune configuration trouvée." }
// or any other case where no saved configuration is returned. Mirrors the
// initial values rendered by each tab component (Divers, Tables, Commandes,
// Notifications) so the modal opens on a coherent baseline.
const initialState: ServioState = {
    langues: {
        enabledLanguages: {
            fr: true,
            en: true,
            ru: true,
            it: true,
            es: true,
            pt: true,
            de: true,
            ar: true,
        },
        defaultLanguage: "fr",
    },

    home: {
        titleEnabled: true,
        filters: {
            all: { enabled: true, color: "#2196F3" },
            pending: { enabled: true, color: "#FF9800" },
            unpaid: { enabled: true, color: "#F44336" },
            paid: { enabled: true, color: "#4CAF50" },
            free: { enabled: true, color: "#9E9E9E" },
        },
        sortOptions: {
            pendingFirst: true,
            unpaidFirst: true,
            totalDesc: true,
            totalAsc: true,
            byNumber: true,
        },
    },

    commandes: {
        filters: {
            all: { enabled: true, color: "#333333" },
            pending: { enabled: true, color: "#FF9800" },
            accepted: { enabled: true, color: "#4CAF50" },
            ready: { enabled: true, color: "#2196F3" },
            refused: { enabled: true, color: "#F44336" },
        },
        sortOptions: {
            pendingFirst: true,
            mostRecent: true,
            oldest: true,
            tableNumberAsc: true,
            tableNumberDesc: true,
            totalDesc: true,
            totalAsc: true,
        },
    },

    notifications: {
        origins: {
            all: { enabled: true, color: "#333333" },
            client: { enabled: true, color: "#FF9800" },
            cuisine: { enabled: true, color: "#4CAF50" },
            pos: { enabled: true, color: "#2196F3" },
            paiement: { enabled: true, color: "#9C27B0" },
        },
    },
};

const servioSlice = createSlice({
    name: "servio",
    initialState,
    reducers: {
        // Reset everything back to defaults — used when the modal opens a new
        // shop so stale state from the previous shop doesn't leak through.
        resetServio() {
            return initialState;
        },

        // Generic setter: caller passes any subset of sections and they get
        // merged on top of the current state. Skips undefined keys so callers
        // can safely build the payload from optional API fields without
        // accidentally wiping out valid sections.
        setServio(state, action: PayloadAction<Partial<ServioState>>) {
            for (const key of Object.keys(
                action.payload
            ) as (keyof ServioState)[]) {
                const value = action.payload[key];
                if (value !== undefined) {
                    state[key] = value as ServioState[typeof key];
                }
            }
        },
    },
});

export const { resetServio, setServio } = servioSlice.actions;

export default servioSlice.reducer;
